/**
 * tutorStudentFaces — ce que la vidéo d'un élève du live dit de lui, en chips (pur, sans composant).
 *
 * Les mots et les tons de l'ancienne pastille de tuile (`LiveParticipantIndicator`) et de l'avatar du
 * live (`LiveAvatarM`), sur la chip du livre (`<Badge>`) : une seule chose à la fois. Peint par
 * `<TutorStudentChips>` (la pellicule et la visio du centre ; au téléphone, la bulle ne garde que la main
 * levée et la présence).
 */
import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { tutorPendingLabel } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { type BadgeVariant } from "../../atoms/Badge"
import { type IconName } from "../../atoms/icons/Icon/iconRegistry"

type TutorStudentChip = { text: string; variant: BadgeVariant; tinted?: boolean; iconName?: IconName }

/** Le silence chauffe à 10 min (l'ancien seuil `TIME_SINCE_LAST_CALL_MINUTES`) ; il tiédit à 5. */
const SILENCE_HOT_MINUTES = 10
const SILENCE_WARM_MINUTES = 5

/** L'état de l'élève, en une chip ; rien quand il n'y a rien à dire. La présence passe avant tout. */
export function tutorStudentStateChip(student: UITutorLiveStudent): TutorStudentChip | null {
    if (student.presence === "offline") return { text: "Hors-ligne", variant: "neutral", iconName: "wifi" }
    if (student.presence === "waiting") return { text: "Pas encore là", variant: "neutral", iconName: "clock" }
    switch (student.state) {
        case "hand_raised":
            return { text: student.stateLabel ?? "Main levée", variant: "accent", iconName: "hand-paper" }
        case "exercise_done":
            return { text: student.stateLabel ?? "Exercice rendu", variant: "success", tinted: true, iconName: "check" }
        case "out_of_app":
            return { text: student.stateLabel ?? "Hors appli", variant: "danger", tinted: true, iconName: "ghost" }
        case "silence":
            return { text: student.stateLabel ?? "Silence", variant: "warning", iconName: "volume-mute" }
        case "with_sam":
            return { text: student.stateLabel ?? "Avec Sam", variant: "info", tinted: true, iconName: "comment-dots" }
        case "photo":
            return { text: student.stateLabel ?? "Prend une photo", variant: "neutral", iconName: "camera" }
        default:
            return null
    }
}

/** Sous 10 min, le chrono dit ses secondes (« 4:05 ») ; au-delà, ses minutes (« 12 min », weekly du 03/11, W9). */
export const CHRONO_SECONDS_BELOW = 10 * 60

/** Le chrono, dit : « 4:05 » sous 10 min, « 12 min » au-delà. */
export function tutorStudentChronoText(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds))
    return whole >= CHRONO_SECONDS_BELOW ? `${Math.floor(whole / 60)} min` : formatCountdown(whole, false)
}

/** Une baisse plus grande, à mode inchangé, est un vrai départ (un aller-retour que la salle n'a pas vu), pas un relevé qui corrige. */
export const CHRONO_HELD_AT_MOST = 30

/**
 * Ce que la puce montre, d'un relevé à l'autre : à mode inchangé, les deux chronos ne font que monter (le temps
 * d'échange en Dialogue, le temps depuis le dernier échange en Concentré). Un relevé qui rattrape une estimation
 * trop haute de quelques secondes fait attendre la puce, sans la faire reculer. Un autre élève (la même puce sert à
 * l'élève suivant au centre), un autre mode ou une grande baisse repartent de leur propre relevé.
 */
export function tutorStudentChronoHeld(
    previous: { id: string; kind: string; seconds: number } | null,
    id: string,
    kind: string,
    seconds: number
): number {
    if (previous == null || previous.id !== id || previous.kind !== kind) return seconds
    return previous.seconds - seconds <= CHRONO_HELD_AT_MOST ? Math.max(previous.seconds, seconds) : seconds
}

/**
 * Le chrono qui chauffe : en Dialogue, le temps d'échange (au vert) ; en Concentré, le temps depuis
 * le dernier échange — gris, puis orange à 5 min, puis rouge à 10. Pas de chrono pour un élève attendu.
 * `seconds` : là où il en est à l'instant (la puce le fait avancer) ; absent, celles du relevé.
 */
export function tutorStudentChronoChip(student: UITutorLiveStudent, seconds?: number): TutorStudentChip {
    const { kind } = student.chrono
    const total = seconds ?? student.chrono.seconds
    const minutes = Math.floor(total / 60)
    const text = tutorStudentChronoText(total)
    if (kind === "exchange") return { text, variant: "success", tinted: true, iconName: "volume-up" }
    if (minutes >= SILENCE_HOT_MINUTES) return { text, variant: "danger", tinted: true, iconName: "stopwatch" }
    if (minutes >= SILENCE_WARM_MINUTES) return { text, variant: "warning", iconName: "stopwatch" }
    return { text, variant: "neutral", iconName: "stopwatch" }
}

/** Ce que le chrono veut dire, pour le lecteur d'écran (les libellés des anciennes infobulles). */
export function tutorStudentChronoLabel(student: UITutorLiveStudent): string {
    return student.chrono.kind === "exchange" ? "Temps d'échange" : "Dernier échange"
}

/**
 * Ce qu'il y a à aller voir chez lui, en mots (weekly du 03/11, W5) : ce qui attend une décision dans sa session
 * (le bac « À décider » : sa main levée, son rendu, son silence, hors appli y ont leur carte) et ses messages non
 * lus. Non vide : la pastille rouge de sa carte, et ces mots pour le lecteur d'écran. Une seule source : la
 * pastille et le bac ne se contredisent jamais (une carte écartée l'éteint), et rien ne se dit deux fois.
 */
export function tutorStudentAttention(student: UITutorLiveStudent): string[] {
    const reasons: string[] = []
    const pending = student.pending ?? 0
    if (pending > 0) reasons.push(tutorPendingLabel(pending))
    const unread = student.unread ?? 0
    if (unread > 0) reasons.push(countLabel(unread, "message non lu", "messages non lus"))
    return reasons
}

/** La classe, ou « Masteur » quand elle manque, comme l'ancienne fiche. */
export function tutorStudentGrade(student: UITutorLiveStudent): string {
    return student.grade ?? "Masteur"
}
